// ============================================================
// ANNEX WINDOW — the underground counterpart to WindowsCard.
//
// Same STRUCTURE as ../WindowsComponentCard/WindowsCard:
//   * a react-draggable window dragged by its title bar
//   * a "program header" carrying a program label + control(s)
//   * a MINIMIZE control that collapses the body down to just the
//     header, and restores it
//   * the art/feature component mounted inside the body
//
// What changes is only the SKIN. The Annex keeps the surface's
// design cues (riveted metal chrome, a program label, a header
// control button) but drains the color: muted slate, rust,
// gunmetal, dim phosphor — weathered, rugged, underground — rather
// than the brighter surface chrome.
//
// Three DISTINCT header characters are chosen by `variant`:
//   1 = rugged riveted gunmetal/steel bar
//   2 = worn stencil / label-tape tag header
//   3 = scratched dead-CRT bezel header
//
// Props:
//   programText : the program label shown in the header
//   artComp     : the feature/art component mounted in the body
//   variant     : 1 | 2 | 3 — selects the header character
//   id          : optional id forwarded to the window root
//   className   : optional extra class on the window root
//
// Drag + minimize behavior mirrors WindowsCard exactly so the
// feature components inside behave identically to the surface.
// All motion honors prefers-reduced-motion via CSS.
// ============================================================

import * as React from "react";
import "./annexwindow.css";
import { useState, useCallback } from "react";
import Draggable from "react-draggable";

// A small fixed pool of slightly-skewed bolt rotations, seeded once at
// module load so rivets don't jitter on re-render (mirrors Annex's Rivet).
const BOLT_ROT = [7, 21, 13, 4, 17, 9];

function HeaderBolt(props) {
    const deg = BOLT_ROT[(props.i || 0) % BOLT_ROT.length];
    return (
        <span
            className="aw-bolt"
            aria-hidden="true"
            style={{ transform: "rotateZ(" + deg + "deg)" }}
        />
    );
}

function AnnexWindow(props) {
    // Normalize the variant to 1..3 (default 1) so a stray value can't
    // produce an unstyled header.
    let v = parseInt(props.variant, 10);
    if (v !== 1 && v !== 2 && v !== 3) {
        v = 1;
    }

    const [minimized, setMinimized] = useState(false);

    const handleMinimize = useCallback(
        function (e) {
            // Keep the header's drag handle from also starting a drag when
            // the control is tapped.
            if (e && e.stopPropagation) {
                e.stopPropagation();
            }
            // On touch, preventDefault on touchEnd stops the browser from also
            // firing a synthesized click that would immediately toggle back.
            if (e && e.type === "touchend" && e.preventDefault) {
                e.preventDefault();
            }
            setMinimized(function (m) {
                return !m;
            });
        },
        []
    );

    // Lift this window above its neighbors while it's being dragged, the same
    // way WindowsCard bumps z-index on drag — but via React state instead of
    // jQuery so we don't reach outside this component.
    const [above, setAbove] = useState(false);
    const handleStart = useCallback(function () {
        setAbove(true);
    }, []);

    const rootClass =
        "aw-window aw-variant-" +
        v +
        (minimized ? " aw-minimized" : "") +
        (above ? " aw-above" : "") +
        (props.className ? " " + props.className : "");

    // The minimize control. Its inner glyph flips between a "collapse" and an
    // "expand" mark so the state reads at a glance, echoing WindowsCard's
    // color-swapping minimize lamp.
    const minimizeBtn = (
        <button
            type="button"
            className="aw-minimize-btn"
            aria-pressed={minimized}
            aria-label={
                (minimized ? "Restore" : "Minimize") +
                " " +
                (props.programText || "program")
            }
            title={minimized ? "Restore" : "Minimize"}
            onClick={handleMinimize}
            onTouchEnd={handleMinimize}
        >
            <span className="aw-minimize-glyph" aria-hidden="true">
                {minimized ? "+" : "–"}
            </span>
        </button>
    );

    // ---- Header skins -------------------------------------------------
    // Each variant keeps the same three structural slots WindowsCard has:
    // a label cluster, identifying chrome, and a control button — but wears
    // a different weathered texture.
    let header;
    if (v === 1) {
        // Variant 1 — rugged riveted gunmetal/steel bar. Bolts at both ends,
        // a stamped program plate in the middle.
        header = (
            <div className="aw-handle aw-handle--steel">
                <span className="aw-bolt-cluster aw-bolt-cluster--left">
                    <HeaderBolt i={0} />
                    <HeaderBolt i={1} />
                </span>
                <span className="aw-label aw-label--stamped">
                    <span className="aw-label-text">{props.programText}</span>
                </span>
                <span className="aw-header-controls">
                    <span className="aw-bolt-cluster aw-bolt-cluster--right">
                        <HeaderBolt i={2} />
                    </span>
                    {minimizeBtn}
                </span>
            </div>
        );
    } else if (v === 2) {
        // Variant 2 — worn stencil / label-tape tag header. A strip of
        // embossed label-tape slapped across a dim slab, edges frayed.
        header = (
            <div className="aw-handle aw-handle--tape">
                <span className="aw-tape">
                    <span className="aw-tape-text">{props.programText}</span>
                </span>
                <span className="aw-header-controls">{minimizeBtn}</span>
            </div>
        );
    } else {
        // Variant 3 — scratched dead-CRT bezel header. A dim phosphor readout
        // behind a scuffed plastic bezel, with a power-lamp control.
        header = (
            <div className="aw-handle aw-handle--bezel">
                <span className="aw-bezel-lamp" aria-hidden="true" />
                <span className="aw-label aw-label--phosphor">
                    <span className="aw-label-text">{props.programText}</span>
                    <span className="aw-bezel-scratch" aria-hidden="true" />
                </span>
                <span className="aw-header-controls">{minimizeBtn}</span>
            </div>
        );
    }

    return (
        <Draggable
            handle=".aw-handle"
            defaultPosition={{ x: 0, y: 0 }}
            scale={1}
            onStart={handleStart}
        >
            <div id={props.id} className={rootClass}>
                {header}
                <div
                    className="aw-program-wrapper"
                    aria-hidden={minimized}
                    style={{ display: minimized ? "none" : "block" }}
                >
                    {props.artComp}
                </div>
            </div>
        </Draggable>
    );
}

export default AnnexWindow;
